import {
  Button,
  FileButton,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm } from '@mantine/form'
import type { WarrantyMetadata } from '@markku/types'
import { IconCalendar, IconFileUpload, IconPlus } from '@tabler/icons-react'
import { AppModal } from '../AppModal'

interface WarrantyModalProps {
  opened: boolean
  onClose: () => void
  onSubmit: (data: {
    file: File
    metadata: Omit<WarrantyMetadata, 'category'>
  }) => Promise<void>
  loading?: boolean
}

export function WarrantyModal({
  opened,
  onClose,
  onSubmit,
  loading = false,
}: WarrantyModalProps) {
  const form = useForm({
    initialValues: {
      deviceName: '',
      file: null as File | null,
      expiryDate: null as Date | null,
      purchaseDate: null as Date | null,
      seller: '',
      notes: '',
    },
    validate: {
      deviceName: (value) => (!value?.trim() ? 'Anna laitteen nimi' : null),
      file: (value) => (!value ? 'Lisää takuutodistus (kuva tai PDF)' : null),
      expiryDate: (value) => (!value ? 'Anna takuun päättymispäivä' : null),
    },
  })

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const handleSubmit = async (values: typeof form.values) => {
    // Form validation already ensures file and required fields exist

    // Convert to Date if needed and validate
    const expiryDate = values.expiryDate ? new Date(values.expiryDate) : null
    const purchaseDate = values.purchaseDate
      ? new Date(values.purchaseDate)
      : null

    if (!expiryDate || isNaN(expiryDate.getTime())) {
      form.setFieldError('expiryDate', 'Anna kelvollinen päivämäärä')
      return
    }

    await onSubmit({
      file: values.file!,
      metadata: {
        deviceName: values.deviceName.trim(),
        expiryDate: expiryDate.toISOString(),
        purchaseDate:
          purchaseDate && !isNaN(purchaseDate.getTime())
            ? purchaseDate.toISOString()
            : undefined,
        seller: values.seller || undefined,
        notes: values.notes || undefined,
      },
    })

    handleClose()
  }

  return (
    <AppModal
      opened={opened}
      onClose={handleClose}
      title="Lisää takuutodistus"
      size="md"
      confirmProps={{
        label: 'Tallenna',
        onClick: form.onSubmit(handleSubmit),
        loading,
      }}
      cancelProps={{
        label: 'Peruuta',
      }}
    >
      <form>
        <Stack gap="md">
          <TextInput
            label="Laitteen nimi"
            placeholder="esim. Mitsubishi MSZ-LN35VG lämpöpumppu"
            required
            {...form.getInputProps('deviceName')}
          />

          <DateInput
            label="Takuu päättyy"
            placeholder="Valitse päivämäärä"
            leftSection={<IconCalendar size={16} />}
            required
            {...form.getInputProps('expiryDate')}
          />

          <DateInput
            label="Ostettu (valinnainen)"
            placeholder="Valitse päivämäärä"
            leftSection={<IconCalendar size={16} />}
            maxDate={new Date()}
            {...form.getInputProps('purchaseDate')}
          />

          <TextInput
            label="Mistä ostettu (valinnainen)"
            placeholder="esim. Gigantti"
            {...form.getInputProps('seller')}
          />

          <Textarea
            label="Huomiot (valinnainen)"
            placeholder="Lisätietoja takuusta..."
            rows={3}
            {...form.getInputProps('notes')}
          />

          <FileButton
            onChange={(file) => form.setFieldValue('file', file)}
            accept="image/*,application/pdf"
          >
            {(props) => (
              <Button
                {...props}
                variant={form.values.file ? 'light' : 'outline'}
                color={form.values.file ? 'green' : 'gray'}
                leftSection={
                  form.values.file ? (
                    <IconPlus size={16} />
                  ) : (
                    <IconFileUpload size={16} />
                  )
                }
                fullWidth
              >
                {form.values.file
                  ? `Tiedosto: ${form.values.file.name}`
                  : 'Liitä takuutodistus (kuva tai PDF)'}
              </Button>
            )}
          </FileButton>

          {form.errors.file && (
            <Text size="xs" c="red" ta="center">
              {form.errors.file}
            </Text>
          )}

          {form.values.file && !form.errors.file && (
            <Text size="xs" c="dimmed" ta="center">
              Voit vaihtaa tiedoston klikkaamalla painiketta uudelleen
            </Text>
          )}
        </Stack>
      </form>
    </AppModal>
  )
}
